/**
 * Gets the type of an item from an array.
 *
 * Note: This does not support tuples
 */
type ItemFromArray<TArray extends unknown[]> = TArray extends []
  ? never
  : TArray[0];

/**
 * Gets the type of the provided object property
 */
type KeyInObject<TObject, K extends keyof TObject> = TObject[K] extends
  | string
  | number
  | symbol
  ? TObject[K]
  : never;

/**
 *
 * Converts an array to an object of the desired key
 *
 * @param arr The array of items. Cannot be a tuple
 * @param key The key of the array items that should become the key of the object
 * @returns an object whose keys represent match the value of the key provided
 *
 * @example
 *
 * const example = [
 *   {
 *     bar: "hello",
 *   },
 *   {
 *     bar: "world"
 *   }
 * ];
 *
 * const myObj = arrayToKeyedObject(example, "bar");
 *
 * {
 *   hello: {
 *      bar: "hello"
 *   },
 *   world: {
 *     bar: "world"
 *   }
 * }
 *
 *
 */
export const arrayToKeyedObject = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TArray extends any[],
  Key extends keyof ItemFromArray<TArray>,
>(
  arr: TArray,
  key: Key,
) => {
  const obj = <
    Record<KeyInObject<ItemFromArray<TArray>, Key>, ItemFromArray<TArray>>
  >{};

  for (const el of arr) {
    const value = el[key] as KeyInObject<ItemFromArray<TArray>, Key>;
    obj[value] = el;
  }

  return obj;
};
